/**
 * Pure SEIS CSV mapping/normalization helpers (no Firebase).
 */

/** SEIS date fields stored as YYYY-MM-DD in Firestore. */
const SEIS_DATE_FIELDS = new Set([
  'reviewDate',
  'reevalDate',
  'meetingDate',
  'dob',
  'affirmedIepDate',
  'initialConsentDate',
  'amendmentAffirmedDate',
  'amendmentDate',
  'currentIepMeetingDate',
  'futureIepMeetingDate'
])

/**
 * CSV headers left UNCHECKED on SEIS import — everything else defaults on.
 *
 * Checking a column only writes it to the `seis.*` reference layer; whether it
 * reaches `app.*` is decided by the school's import-flow (source-of-truth)
 * settings. So this list is "what to pull in", not the safety gate it used to
 * be, and an allowlist here just meant re-checking the same boxes every import.
 *
 * Date of Birth is the lone exclusion: Aeries owns identity, so a second copy
 * of a birth date is sensitive data with no consumer. SEIS ID is kept — it is
 * SEIS's stable record key and the durable back-reference for reconciling a
 * student against the SEIS side when SSID is wrong.
 *
 * SSID is always imported for student matching even when unchecked.
 */
const SEIS_NON_DEFAULT_IMPORT_HEADERS = new Set(['date of birth', 'dob'])

function toCamelCase(fieldName) {
  return fieldName
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase())
    .replace(/^[A-Z]/, (match) => match.toLowerCase())
}

/**
 * Whether a SEIS CSV header should be checked by default in the import UI.
 */
export function isDefaultSeisImportField(csvFieldName) {
  const header = String(csvFieldName || '')
    .trim()
    .toLowerCase()
  if (!header) return false
  return !SEIS_NON_DEFAULT_IMPORT_HEADERS.has(header)
}

function isSeisEmptySentinel(value) {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
  return !normalized || normalized === 'none' || normalized === 'n/a' || normalized === 'na'
}

/**
 * Normalize SEIS date strings (MM/DD/YYYY or ISO) to YYYY-MM-DD.
 */
export function normalizeSeisDate(value) {
  if (value == null) return ''
  const trimmed = String(value).trim()
  if (!trimmed) return ''

  // Amendment cells often list several dates. Keep the latest. A list used to
  // store as blank, which dropped the cell.
  if (trimmed.includes(',')) {
    const parsed = trimmed
      .split(',')
      .map((part) => normalizeSeisDate(part))
      .filter(Boolean)
      .sort()
    return parsed.length ? parsed[parsed.length - 1] : ''
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed
  }

  const mdy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (mdy) {
    const [, month, day, year] = mdy
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }

  const parsed = new Date(trimmed)
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0]
  }

  return ''
}

/** Spelled-out SEIS grade words → app grade strings (app uses "7", "8", "K", …). */
const SEIS_GRADE_WORDS = {
  preschool: 'PS',
  'pre-k': 'PS',
  prek: 'PS',
  'transitional kindergarten': 'TK',
  tk: 'TK',
  kindergarten: 'K',
  k: 'K',
  first: '1',
  second: '2',
  third: '3',
  fourth: '4',
  fifth: '5',
  sixth: '6',
  seventh: '7',
  eighth: '8',
  ninth: '9',
  tenth: '10',
  eleventh: '11',
  twelfth: '12'
}

/**
 * Normalize a SEIS grade value to the app's grade format.
 * Handles "Eighth grade", "8th grade", "Grade 8", "08", and "8".
 * Unrecognized values pass through trimmed so nothing is silently dropped.
 */
export function normalizeSeisGrade(value) {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''

  const lower = trimmed
    .toLowerCase()
    .replace(/\bgrade\b/g, '')
    .trim()

  if (SEIS_GRADE_WORDS[lower]) return SEIS_GRADE_WORDS[lower]

  // "8th" → "8"
  const ordinal = lower.match(/^(\d{1,2})(st|nd|rd|th)$/)
  if (ordinal) return String(Number(ordinal[1]))

  // "08" / "8" → "8"
  if (/^\d{1,2}$/.test(lower)) return String(Number(lower))

  return trimmed
}

/**
 * Meeting Type cells look like "20|" or "20|40|". The trailing pipe is empty.
 * Codes: 10 initial (already the active IEP), 20 annual, 30 pending initial,
 * 40 eligibility evaluation.
 * @returns {string[]}
 */
export function parseMeetingTypeCodes(value) {
  return String(value ?? '')
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean)
}

/** Stored form of a Meeting Type cell: "20" or "20, 40". */
export function normalizeMeetingType(value) {
  return parseMeetingTypeCodes(value).join(', ')
}

/**
 * Maps SEIS CSV field names to app field names.
 */
export function mapSeisField(csvFieldName) {
  const fieldMap = {
    SSID: 'ssid',
    'SEIS ID': 'seisId',
    FirstName: 'firstName',
    'First Name': 'firstName',
    LastName: 'lastName',
    'Last Name': 'lastName',
    MiddleName: 'middleName',
    'Middle Name': 'middleName',
    'Preferred Name': 'preferredName',
    Grade: 'grade',
    'Grade Level': 'grade',
    DOB: 'dob',
    'Date of Birth': 'dob',
    Gender: 'gender',
    Ethnicity: 'ethnicity',
    School: 'school',
    'School of Attendance': 'school',
    'School of Residence': 'school',
    'School CDS Code': 'schoolCdsCode',
    'School of Attendance CDS Code': 'schoolCdsCode',
    'School of Residence Code': 'schoolCdsCode',
    'Primary Language': 'primaryLanguage',
    'Reporting LEA': 'reportingLea',
    DSEA: 'dsea',
    Address: 'address',
    City: 'city',
    State: 'state',
    ZipCode: 'zipCode',
    Phone: 'phone',
    ParentName: 'parentName',
    ParentPhone: 'parentPhone',
    ParentEmail: 'parentEmail',
    ELL: 'ell',
    504: '504',
    IEP: 'iep',
    CaseManager: 'caseManager',
    'Case Manager': 'caseManager',
    'Case Manager Email': 'caseManagerEmail',
    Disability: 'disability',
    'Disability 1': 'disability',
    'Disability 2': 'secondaryDisability',
    PrimaryDisability: 'primaryDisability',
    SecondaryDisability: 'secondaryDisability',
    Eligibility: 'eligibilityCategory',
    EligibilityCategory: 'eligibilityCategory',
    // Workflow status — not the disability category. Do not fold these into
    // eligibilityCategory. See IMPORT_PLAN_EVIDENCE.md.
    'Student Eligibility Status': 'studentEligibilityStatus',
    StudentEligibilityStatus: 'studentEligibilityStatus',
    'Student Exited': 'studentExited',
    StudentExited: 'studentExited',
    ServiceMinutes: 'serviceMinutes',
    Goals: 'goals',
    Notes: 'notes',
    Plan: 'plan',
    'Plan Type': 'planType',
    PlanType: 'planType',
    'Type of Plan': 'planType',
    'Plan Type Code': 'planType',
    // Exactly as the SEIS export ships it.
    'Plan Type (Edu Plan for SpEd Svcs)': 'planType',
    ReviewDate: 'reviewDate',
    'Next Plan Review': 'reviewDate',
    'Date of Next Annual Plan Review': 'reviewDate',
    Annual_Review_Date: 'reviewDate',
    ReevalDate: 'reevalDate',
    'Next Reevaluation': 'reevalDate',
    'Date of Next Reevaluation': 'reevalDate',
    Triennial_Date: 'reevalDate',
    'Date of Affirmed Current IEP (Date Processed)': 'affirmedIepDate',
    'Date of Affirmed Current IEP': 'affirmedIepDate',
    'Date of Initial Parent Consent': 'initialConsentDate',
    'Signature Current IEP': 'iepSignedCurrent',
    'Parent/Legal Guardian Signature on Current IEP': 'iepSignedCurrent',
    'Date of Affirmed Amendment': 'amendmentAffirmedDate',
    'Date Amendment was affirmed': 'amendmentAffirmedDate',
    'Date of Amendment': 'amendmentDate',
    'SpEd Type': 'spedType',
    BIP: 'bip',
    'Meeting Type': 'meetingType',
    'Date of IEP (Meeting Date on Current IEP Forms)': 'currentIepMeetingDate',
    'Date of IEP (Meeting Date on Future IEP forms)': 'futureIepMeetingDate',
    // seis.* reference only — app.dates.meetingDate is app/calendar-owned (see SEIS_APP_DATE_FIELDS)
    MeetingDate: 'meetingDate',
    IEP_Start_Date: 'meetingDate',
    SpeechProvider: 'speechProvider',
    MHProvider: 'mhProvider',
    OTProvider: 'otProvider',
    ProgramAccommodations: 'programAccommodations',
    Accommodations: 'programAccommodations',
    IEP_Services: 'iepServices'
  }

  return fieldMap[csvFieldName] || toCamelCase(csvFieldName)
}

/**
 * Normalize a single SEIS field value after header mapping.
 */
export function normalizeSeisImportValue(csvFieldName, mappedFieldName, rawValue) {
  if (rawValue == null) return ''

  if (
    csvFieldName === 'IEP_Services' ||
    csvFieldName === 'iep_services' ||
    mappedFieldName === 'iepServices'
  ) {
    return String(rawValue)
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)
  }

  if (
    (csvFieldName === 'ProgramAccommodations' ||
      csvFieldName === 'Accommodations' ||
      mappedFieldName === 'programAccommodations') &&
    typeof rawValue === 'string' &&
    rawValue.includes(';')
  ) {
    return rawValue
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim()
    if (isSeisEmptySentinel(trimmed)) return ''

    if (mappedFieldName === 'meetingType') {
      return normalizeMeetingType(trimmed)
    }

    if (SEIS_DATE_FIELDS.has(mappedFieldName)) {
      return normalizeSeisDate(trimmed)
    }

    if (mappedFieldName === 'grade') {
      return normalizeSeisGrade(trimmed)
    }

    if (
      mappedFieldName === 'disability' ||
      mappedFieldName === 'secondaryDisability' ||
      mappedFieldName === 'primaryDisability'
    ) {
      return trimmed.replace(/\s*\*+\s*$/, '').trim()
    }

    if (mappedFieldName === 'schoolCdsCode') {
      const digits = trimmed.replace(/\D/g, '')
      if (!digits) return ''
      // SEIS may export the 7-digit school portion (e.g. 6060180) or full 14-digit CDS.
      return digits.length <= 7 ? digits.padStart(7, '0') : digits
    }

    return trimmed
  }

  return rawValue
}

/**
 * Date fields SEIS is the source of truth for.
 * meetingDate is deliberately excluded: held meeting dates are owned by the
 * app / Google Calendar (meeting manager), never by SEIS imports.
 */
const SEIS_APP_DATE_FIELDS = ['reviewDate', 'reevalDate']

/**
 * Build the app.dates patch for a SEIS import row.
 *
 * getDisplayValue gives app.dates.* priority over seis.*, so stale app.dates
 * (e.g. carried over by a legacy backup transfer) mask freshly imported SEIS
 * dates everywhere in the UI. The import therefore overwrites app.dates with
 * any non-empty imported date values.
 */
export function buildSeisDatesPatch(seisData = {}) {
  const patch = {}
  for (const field of SEIS_APP_DATE_FIELDS) {
    const value = seisData[field]
    if (value != null && String(value).trim() !== '') patch[field] = value
  }
  return patch
}

/**
 * Build a patch that fills EMPTY app.studentData identity fields from SEIS.
 *
 * Used to repair students created by an earlier SEIS import that didn't
 * include name columns (they exist in the DB but show as blank rows).
 * Never overwrites a non-empty value — Aeries/manual identity always wins.
 */
export function buildIdentityBackfillPatch(existingStudentData = {}, seisData = {}) {
  const patch = {}
  for (const key of ['firstName', 'lastName', 'grade']) {
    const existing = existingStudentData?.[key]
    const incoming = seisData?.[key]
    if (
      (existing == null || String(existing).trim() === '') &&
      incoming != null &&
      String(incoming).trim() !== ''
    ) {
      patch[key] = String(incoming).trim()
    }
  }
  return patch
}

/**
 * Find the SSID value on a parsed import row. Header match mirrors the
 * preview's acceptance check (trimmed, case-insensitive) so a column the
 * preview accepted can never be skipped here over casing/whitespace.
 */
export function extractSsidFromImportRow(entry = {}) {
  for (const [key, value] of Object.entries(entry)) {
    if (key.trim().toLowerCase() === 'ssid' && value != null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

/**
 * Ensures SSID is always included in the fields sent to import.
 */
export function ensureSeisImportFields(selectedFields, availableFields = []) {
  const fields = [...selectedFields]
  const ssidField = availableFields.find((f) => f.trim().toLowerCase() === 'ssid')
  if (ssidField && !fields.includes(ssidField)) {
    fields.unshift(ssidField)
  }
  return fields
}
